// combat-start-prompt.ts — the GM "should this start a fight?" prompt, decided in the browser.
//
// OWNER RULING (2026-09-02). Nothing in the engine ever rolls initiative on its own. When someone
// acts against an opposing team while out of combat, the GM is OFFERED a button and decides.
//
// WHY THIS IS FRONT END. Every input is already here:
//   * The GM's websocket channel receives EVERY roll — including roll_private=2 secrets, so the
//     sneak-vs-attention contest arrives too (insert_rolls_inc.php: "Always publish to :gm ... GM
//     sees all rolls including secrets, across all encounters").
//   * A roll row names its pair (roll_by / roll_to) and, on the contestee's row, carries the
//     caster's result as roll_dc — so the contest is scored from ONE row, no pairing needed
//     (contesting_roll_helpers.php: contesting_roll_from_target sets roll_dc = caster result).
//   * Combat membership and teams live on the encounter the client already holds.
// So the server does no work for this: no layer reads, no INSERT, no log row. The prompt is
// ephemeral by design — it is a question about this moment, not a record.
//
// THE ONE THING THE SERVER STILL PUSHES is the stealth SPOT decision
// (stealth_spotted_gm_prompt). That verdict is not in the rolls: ft_stealth_evaluate_pair compares
// the cached Attention roll plus distance-band and cover modifiers against Sneak, re-evaluated per
// movement step. Scoring the two raw rolls here would disagree with the server. So we consume that
// row rather than re-deriving it.
//
// NO LINE OF SIGHT is an automatic caster loss the dice never show: damage_roll.php reads
// contest_cover.autoWin and bumps the DC in a local, and advStacks is 0 for the 'blocked' tier, so
// nothing about it reaches the stored row. We recover it by asking the geometry ourselves.
// DIRECTION MATTERS: the engine samples FROM THE CASTER to the contestee's volume
// (ft_contest_cover_context → ft_shape_ray_z($shape, $caster_arr), and its warning reads "No line
// of sight to <target>"), so we cast the same way. Ours is the centre ray where the server samples
// nine, so it errs toward blocked; here that costs one ignorable button, never a missed prompt.
//
// ARRIVAL-TIME, NOT RENDER-TIME. This hangs off handleRollInsert (the WS push), never off the log
// renderer: log-render handlers re-run on every history fetch and every regroup, so deciding there
// would re-prompt and re-pause whenever the GM scrolled the log.

import { prependLogRow } from './log-row-helpers';
import {
    creatureIsInActiveCombat,
    creatureSightLineBlocked,
    opposingTeams,
    type ClientCombatShape,
} from '../roll-init/client-combat-membership';
import { encounterCreatureField, inventoryField } from '../shared/array-types';
import { ftSetCampaignClockRunning } from '../shared/campaign-clock-transport';

// RollType UUIDs (includes/additions.php, final class RollType). Stable catalog constants.
const ROLL_TYPE_DAMAGE = '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0';
const ROLL_TYPE_CONTESTING = 'AD80D31D-898B-66CE-0894-1335D11B1529';
const ATTACK_ROLL_TYPES = new Set([
    '2FE8DFB7-FE61-4862-AF71-3523262350A0', // weapon_melee_attack_roll
    '3F85CA97-F9B2-A83A-FDE8-E523FD99346C', // weapon_ranged_attack_roll
    '5740CA49-006E-A7A0-B46B-6DDED6CD546C', // spell_melee_attack_roll
    'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C', // spell_ranged_attack_roll
]);

// The server's own spotted prompt — we pause for it and let log-render draw it.
const STEALTH_SPOTTED_PROMPT = 'stealth_spotted_gm_prompt';

type PromptReason = 'attack' | 'contest_lost';
// 'contest_won_pending_los' is not a prompt reason — it is a contest the caster won on the dice,
// which only becomes a loss if the contestee turns out to have no line of sight.
type RollVerdict = PromptReason | 'contest_won_pending_los';

// Dedupe is ACTION-scoped, not pair-scoped. One action shares one roll_id across every row it
// emits — an attack and its damage roll, and a mass contest's row per target — so keying on
// roll_id yields exactly one button per action however many rows arrive (a pair key gave a mass
// contest one prompt PER TARGET). It also expires by construction: the next action is a new
// roll_id, so nothing is muted for the life of the page the way a pair memo was.
const _promptedActions = new Set<string>();
// Belt to that braces: a caster swinging repeatedly should not stack a row per swing. Short, so a
// later scene is never silenced — the failure mode of the old memo.
const CASTER_PROMPT_COOLDOWN_MS = 20000;
const _casterPromptedAt = new Map<string, number>();
// Pause is keyed separately: the stealth row arrives already-decided and has its own roll_id.
const _pausedForRollIds = new Set<string>();
const _encounterReads = new Map<string, Promise<Record<string, unknown> | null>>();

/** Is this the encounter the GM is looking at? Unknown → false, because the button assumes it. */
function isActiveEncounter(encounterId: string): boolean {
    const activeEncounterId = String(window.ftGetActiveEncounterId?.() ?? '').trim();
    return activeEncounterId !== '' && activeEncounterId.toUpperCase() === encounterId.toUpperCase();
}

function upper(value: unknown): string {
    return String(value ?? '').trim().toUpperCase();
}

function remember(set: Set<string>, key: string, cap: number): boolean {
    if (key === '' || set.has(key)) return false;
    set.add(key);
    if (set.size > cap) {
        const oldest = set.values().next().value;
        if (oldest !== undefined) set.delete(oldest);
    }
    return true;
}

/**
 * Pause campaign time.
 * @returns true only when this call is what stopped it — the prompt tells the GM so.
 *
 * This used to read the pause button's style.display and treat "not 'block'" as "already stopped".
 * That is false whenever the transport cluster is in in-combat chrome (both buttons display:none)
 * while the derived clock is still running, which is precisely the moment a fight starts — so the
 * one thing this function exists to do was skipped exactly when it mattered (2026-09-08).
 */
function pauseCampaignTime(): boolean {
    return ftSetCampaignClockRunning(false);
}

/**
 * Why this roll is worth asking about, or null for the overwhelming majority that are not.
 *
 *   attack       — an attack roll or a damage roll. Outcome-independent: a missed swing is a swing.
 *   contest_lost — a contest the CASTER LOST. This row belongs to the contestee, so its own
 *                  roll_result is the target's total and roll_dc is the caster's; the caster lost
 *                  when roll_dc < roll_result. A WON contest starts nothing — that asymmetry is the
 *                  point. A charmed guard is not a fight; a botched pickpocket might be.
 *
 * Everything else — plain skill rolls, essence rolls, heals, summons — returns null and is silent.
 */
export function classifyRoll(roll: Record<string, unknown>): RollVerdict | null {
    const rollType = upper(roll.roll_type_1);
    if (rollType === '') return null;

    if (rollType === ROLL_TYPE_DAMAGE || ATTACK_ROLL_TYPES.has(rollType)) return 'attack';

    if (rollType === ROLL_TYPE_CONTESTING) {
        const casterTotal = Number(roll.roll_dc ?? 0);
        const contesteeTotal = Number(roll.roll_result ?? 0);
        // roll_dc is only stamped on the contestee's row (contesting_roll_from_target); every
        // caster-side row carries 0 — contesting_roll.php passes roll_dc: 0 explicitly on the
        // ability_dc path, and skill_roll_inc.php defaults it to 0 on the other. That is also what
        // keeps the stealth sneak/attention contests out: they never set a DC.
        // Fail-silent edge: a caster whose total lands at or below 0 (heavy penalties) reads as
        // "not the contestee's row" and is skipped. Erring toward silence is the right direction —
        // the GM's own button always works.
        if (!Number.isFinite(casterTotal) || casterTotal <= 0) return null;
        // Lost on the dice, or lost pending the line-of-sight check the caller runs once it has
        // the geometry (a blocked contestee wins outright however the dice fell).
        return casterTotal < contesteeTotal ? 'contest_lost' : 'contest_won_pending_los';
    }

    return null;
}

/** FT3D's shape truth for the active encounter, or null when the runtime is not on the page. */
function combatShapeTruth(): ClientCombatShape | null {
    // Same selector combat-pick.ts uses for the runtime frame.
    const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
    const frameWindow = frame?.contentWindow as (Window & {
        ft3dGetCombatPreviewTruth?: () => unknown;
    }) | null | undefined;
    let raw: Record<string, unknown> | null | undefined;
    try {
        raw = frameWindow?.ft3dGetCombatPreviewTruth?.() as Record<string, unknown> | null | undefined;
    } catch {
        return null; // cross-origin or runtime not ready — fall back to dice-only scoring
    }
    if (raw?.schema !== 'ft.ft3d.combat-preview-truth.v1') return null;
    const shape = raw.shape as ClientCombatShape | undefined;
    return shape && Array.isArray(shape.boxes) ? shape : null;
}

/**
 * On a contest row the ROLLER is the contestee and roll_to is the caster, so the aggressor and the
 * one acted upon are swapped relative to an attack row. The prompt seeds initiative from whoever
 * acted, so get this the right way round.
 */
export function promptPair(roll: Record<string, unknown>, verdict: RollVerdict): { casterId: string; targetId: string } {
    const roller = upper(roll.roll_by || roll.roll_creature_id);
    const other = upper(roll.roll_to || roll.roll_target_creature_id);
    return verdict === 'attack'
        ? { casterId: roller, targetId: other }
        : { casterId: other, targetId: roller };
}

function findRow(encounter: Record<string, unknown>, creatureId: string): Record<string, unknown> | null {
    const rows = encounter.encounter_creatures_arr;
    if (!Array.isArray(rows)) return null;
    const match = rows.find(row => upper(
        encounterCreatureField((row as { encounter_creatures?: unknown })?.encounter_creatures, 'encounter_creature_id'),
    ) === creatureId);
    return (match as Record<string, unknown> | undefined) ?? null;
}

function creatureName(row: Record<string, unknown> | null, fallback: string): string {
    if (!row || typeof getCreatureName !== 'function') return fallback;
    try {
        return String(getCreatureName(row) || fallback);
    } catch {
        return fallback;
    }
}

function escapeText(value: string): string {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
}

/** Inject the ephemeral prompt row at the top of the sidelog. */
function renderPrompt(
    casterId: string,
    casterName: string,
    targetName: string,
    abilityName: string,
    reason: PromptReason,
    paused: boolean,
): void {
    const what = reason === 'contest_lost'
        ? `failed ${abilityName ? escapeText(abilityName) : 'a contested roll'} against ${escapeText(targetName)}`
        : `attacked ${escapeText(targetName)}${abilityName ? ` with ${escapeText(abilityName)}` : ''}`;
    const pausedNote = paused
        ? '<div class="col-12 text-center small text-body-secondary pt-1">Campaign time paused while you decide.</div>'
        : '';
    prependLogRow(`<li class="list-group-item" data-ft-combat-start-prompt="${escapeText(casterId)}">
        <div class="row m-0">
            <div class="col-12 text-left">${escapeText(casterName)} ${what} out of combat.</div>
            <div class="col-12 pt-2 text-center">
                <button type="button" class="btn btn-sm btn-warning"
                    data-ft-log-action="roll-initiative-from-seed"
                    data-ft-creature-id="${escapeText(casterId)}">Roll initiative from ${escapeText(casterName)}?</button>
            </div>
            ${pausedNote}
        </div>
    </li>`);
}

/**
 * A roll arrived. Decide whether the GM should be asked to start a fight, and if so pause the clock
 * and draw the offer.
 *
 * Returns true when the roll passed the SYNCHRONOUS gates and a decision was scheduled — not that a
 * prompt appeared. The async tail can still reject it (rows missing, same team, already fighting,
 * clear line of sight, no sidelog). Nothing consumes the return today; it exists for tests.
 */
export function handleCombatStartPrompt(roll: Record<string, unknown> | undefined): boolean {
    if (!roll) return false;
    if (!window.FateTide?.user?.isGM) return false;

    // The server's stealth verdict: pause and let log-render draw its row. Nothing to decide here,
    // but the same two gates apply as below — the row only REACHES the sidelog when it belongs to
    // the encounter the GM is viewing and that log exists, and stopping the clock with nothing on
    // screen to explain it is worse than not stopping it.
    if (String(roll.roll_type_1 ?? '') === STEALTH_SPOTTED_PROMPT) {
        if (!isActiveEncounter(String(roll.encounter_id ?? '').trim())) return false;
        if (!document.getElementById('div_sidelog_results')) return false;
        if (remember(_pausedForRollIds, upper(roll.roll_id), 100)) pauseCampaignTime();
        return false;
    }

    const verdict = classifyRoll(roll);
    if (!verdict) return false;

    const { casterId, targetId } = promptPair(roll, verdict);
    if (casterId === '' || targetId === '' || casterId === targetId) return false;

    const encounterId = String(roll.encounter_id ?? '').trim();
    if (!encounterId) return false;

    // The :gm channel carries rolls from EVERY encounter, but the button posts the SIDELOG's
    // encounter_id (roll-init.ts postRollInitiative reads getSidelogContext), not this roll's. A
    // prompt raised for another room would therefore seed a creature from encounter A into a
    // roll_initiative call against encounter B. Scope to what the GM is actually looking at, and
    // FAIL CLOSED when that is unknown — an unscoped prompt is the wrong-encounter bug.
    if (!isActiveEncounter(encounterId)) return false;

    // The GM tool's user stub and floor objects cannot seed initiative. The held roster
    // also answers combat membership before any encounter read can install another snapshot.
    const viewer = String(window.FateTide?.user?.id ?? '').trim().toUpperCase();
    if (viewer !== '' && casterId.toUpperCase() === viewer) return false;
    const cached = (window.FateTide?.encounterCache as Map<string, unknown> | undefined)?.get?.(encounterId) as Record<string, unknown> | undefined;
    const casterRow = cached && findRow(cached, casterId);
    if (casterRow && creatureIsInActiveCombat(cached!, casterRow as never)) return false;
    const actionKey = `${encounterId}|${casterId}|${upper(roll.roll_id)}`;
    if (_promptedActions.has(actionKey)) return false;
    const lastPrompted = _casterPromptedAt.get(`${encounterId}|${casterId}`);
    if (lastPrompted !== undefined && Date.now() - lastPrompted < CASTER_PROMPT_COOLDOWN_MS) return false;
    const floor = Array.isArray(cached?.encounter_inventories_arr) ? cached!.encounter_inventories_arr as unknown[] : [];
    const isObject = (id: string): boolean => floor.some(row => String(inventoryField(row, 'inventory_id') ?? '').toUpperCase() === id.toUpperCase());
    if (isObject(targetId) || isObject(casterId)) return false;

    void decideAndPrompt(encounterId, casterId, targetId, roll, verdict);
    return true;
}

/**
 * The encounter read is async, so the decision tail runs off the WS frame. Nothing downstream
 * depends on its result — a missed prompt costs the GM one click on the Roll Initiative button
 * they already have.
 *
 * Qualifying rows share the normal page reader. Held combat membership and completed prompt
 * gates have already ruled out routine attacks, before any read can install another encounter.
 */
async function decideAndPrompt(
    encounterId: string,
    casterId: string,
    targetId: string,
    roll: Record<string, unknown>,
    verdict: RollVerdict,
): Promise<void> {
    if (typeof getEncounterArr !== 'function') return;
    let encounter: Record<string, unknown> | null = null;
    try {
        const key = upper(encounterId);
        let loading = _encounterReads.get(key);
        if (!loading) {
            loading = Promise.resolve().then(() => getEncounterArr(encounterId)) as Promise<Record<string, unknown> | null>;
            _encounterReads.set(key, loading);
            const release = (): void => { if (_encounterReads.get(key) === loading) _encounterReads.delete(key); };
            void loading.then(release, release);
        }
        encounter = await loading;
    } catch {
        return;
    }
    if (!encounter) return;

    const casterRow = findRow(encounter, casterId);
    const targetRow = findRow(encounter, targetId);
    if (!casterRow || !targetRow) return;

    // Already fighting → this is an in-combat action, not the start of one. Membership is the
    // creature's OWN combat_id, never "is any combat open in this encounter".
    if (creatureIsInActiveCombat(encounter, casterRow as never)) return;
    if (!opposingTeams(casterRow as never, targetRow as never)) return;

    // A contest the caster won on the dice is still a loss when there was no line of sight to the
    // contestee — the engine's autoWin, which never reaches the roll row. Ask the geometry, casting
    // from the caster exactly as ft_contest_cover_context does.
    let reason: PromptReason;
    if (verdict === 'contest_won_pending_los') {
        if (!creatureSightLineBlocked(combatShapeTruth(), casterRow as never, targetRow as never)) return;
        reason = 'contest_lost';
    } else {
        reason = verdict;
    }

    // The GM may have switched encounters while the roster fetch was in flight. Re-check before
    // touching the clock or the log: the button reads the sidelog's encounter, so a prompt drawn
    // after a switch would seed this creature into the wrong fight.
    if (!isActiveEncounter(encounterId)) return;

    // No sidelog on this page → prependLogRow is a silent no-op, and pausing anyway would stop the
    // clock with nothing on screen to explain why. Keep the pause and its explanation together.
    if (!document.getElementById('div_sidelog_results')) return;

    // One button per ACTION (shared roll_id), plus a short per-caster cooldown so a flurry of
    // swings does not stack rows. Neither can mute a later scene.
    const actionKey = `${encounterId}|${casterId}|${upper(roll.roll_id)}`;
    if (!remember(_promptedActions, actionKey, 200)) return;

    const casterKey = `${encounterId}|${casterId}`;
    const now = Date.now();
    const lastPrompted = _casterPromptedAt.get(casterKey) ?? 0;
    if (now - lastPrompted < CASTER_PROMPT_COOLDOWN_MS) return;
    _casterPromptedAt.set(casterKey, now);

    const paused = pauseCampaignTime();
    ftDebugLog('combat', { phase: 'combat-start-prompt', reason, casterId, targetId, paused });

    // The roll's own name fields follow the ROLLER, so on a contest row they are swapped just like
    // the ids were. Getting this wrong only shows when getCreatureName is unavailable, which is
    // exactly when the fallback is all the GM has.
    const rollerName = String(roll.roll_creature_name ?? '');
    const otherName = String(roll.roll_target_creature_name ?? '');
    const casterFallback = (reason === 'contest_lost' ? otherName : rollerName) || 'The aggressor';
    const targetFallback = (reason === 'contest_lost' ? rollerName : otherName) || 'an opposing creature';

    renderPrompt(
        casterId,
        creatureName(casterRow, casterFallback),
        creatureName(targetRow, targetFallback),
        String(roll.roll_item_id_item_name ?? ''),
        reason,
        paused,
    );
}
